import { NgModule, CUSTOM_ELEMENTS_SCHEMA } from "@angular/core";
import { BrowserModule } from "@angular/platform-browser";
import { HttpClientModule, HTTP_INTERCEPTORS } from "@angular/common/http";
import { enableProdMode } from "@angular/core";
import { CoreModule } from "./core/core.module";

//Custom Modules
import { AuthenticationModule } from "./authentication/authentication.module";

//Responsive UI
import "@jkhy/responsive-ui-wc/components/rui-buttons/rui-buttons-imports";
import "@jkhy/responsive-ui-wc/components/rui-data-pager/rui-data-pager-imports";
import "@jkhy/responsive-ui-wc/components/rui-dialog-box/rui-dialog-box-imports";
import "@jkhy/responsive-ui-wc/components/rui-header/rui-header-imports";
import "@jkhy/responsive-ui-wc/components/rui-icon/rui-icon-imports";
import "@jkhy/responsive-ui-wc/components/rui-input/rui-input-imports";
import "@jkhy/responsive-ui-wc/components/rui-layout/rui-layout-imports";
import "@jkhy/responsive-ui-wc/components/rui-mega-menu/rui-mega-menu-imports";
import "@jkhy/responsive-ui-wc/components/rui-master-detail/rui-master-detail-imports";
import "@jkhy/responsive-ui-wc/components/rui-nav/rui-nav-imports";
import "@jkhy/responsive-ui-wc/components/rui-notifications/rui-notifications-imports";
import "@jkhy/responsive-ui-wc/components/rui-password-strength-meter/rui-password-strength-meter-imports";
import "@jkhy/responsive-ui-wc/components/rui-percentage-circle/rui-percentage-circle-imports";
import "@jkhy/responsive-ui-wc/components/rui-progress-bar/rui-progress-bar-imports";
import "@jkhy/responsive-ui-wc/components/rui-process-timeline/rui-process-timeline-imports";
import "@jkhy/responsive-ui-wc/components/rui-rating/rui-rating-imports";
import "@jkhy/responsive-ui-wc/components/rui-record-detail/rui-record-detail-imports";
import "@jkhy/responsive-ui-wc/components/rui-session-timeout/rui-session-timeout-imports";
import "@jkhy/responsive-ui-wc/components/rui-tabs/rui-tabs-imports";
import "@jkhy/responsive-ui-wc/components/rui-tile/rui-tile-imports";
import "@jkhy/responsive-ui-wc/components/rui-tooltip/rui-tooltip-imports";
import { JhaButtonsModule } from "@jkhy/responsive-ui-angular/jha-buttons";
import { JhaDialogBoxModule } from "@jkhy/responsive-ui-angular/jha-dialog-box";
import { JhaFloatingToolbarModule } from "@jkhy/responsive-ui-angular/jha-floating-toolbar";
import { JhaFormsModule } from "@jkhy/responsive-ui-angular/jha-forms";
import { JhaHeaderModule } from "@jkhy/responsive-ui-angular/jha-header";
import { JhaIconModule } from "@jkhy/responsive-ui-angular/jha-icon";
import { JhaLayoutModule } from "@jkhy/responsive-ui-angular/jha-layout";
import { JhaMasterDetailModule } from "@jkhy/responsive-ui-angular/jha-master-detail";
import { JhaMegaMenuModule } from "@jkhy/responsive-ui-angular/jha-mega-menu";
import { JhaNavModule } from "@jkhy/responsive-ui-angular/jha-nav";
import {
  JhaNotificationsModule,
  JhaSessionTimeoutService,
  JhaNotificationService,
} from "@jkhy/responsive-ui-angular/jha-notifications";
import { JhaPasswordStrengthMeterModule } from "@jkhy/responsive-ui-angular/jha-password-strength-meter";
import { JhaPercentageCircleModule } from "@jkhy/responsive-ui-angular/jha-percentage-circle";
import { JhaPipesModule } from "@jkhy/responsive-ui-angular/jha-pipes";
import { JhaProcessTimelineModule } from "@jkhy/responsive-ui-angular/jha-process-timeline";
import { JhaProgressBarModule } from "@jkhy/responsive-ui-angular/jha-progress-bar";
import { JhaRatingModule } from "@jkhy/responsive-ui-angular/jha-rating";
import { JhaRecordDetailModule } from "@jkhy/responsive-ui-angular/jha-record-detail";
import { JhaResponsiveCoreModule } from "@jkhy/responsive-ui-angular/jha-responsive-core";
import { JhaTileModule } from "@jkhy/responsive-ui-angular/jha-tile";
import { JhaToggleModule } from "@jkhy/responsive-ui-angular/jha-toggle";
import { JhaUnsavedChangesModule } from "@jkhy/responsive-ui-angular/jha-unsaved-changes";
import { StoreModule } from "@ngrx/store";
import { StoreDevtoolsModule } from "@ngrx/store-devtools";

//PrimeNG
import { TableModule } from "primeng/table";

import { AppComponent } from "./app.component";
import { MainHeaderComponent } from "./core/main-header/main-header.component";
import { MainNavComponent } from "./core/main-nav/main-nav.component";
import { HomeComponent } from "./core/home/home.component";
import { AppRoutingModule } from "./app.routing.module";

//Services
import { LoggedInGuard } from "./services/logged-in.guard";

import { environment } from "../environments/environment";

import { BrowserAnimationsModule } from "@angular/platform-browser/animations";
import { ErrorHandlerModule } from "./core/error-handler/error-handler.module";
import { ErrorInterceptorService } from "./core/error-handler/error-interceptor.service";
import { EffectsModule } from "@ngrx/effects";

import * as fromRoot from "./state";
import { StoreRouterConnectingModule } from "@ngrx/router-store";
import { CustomRouteSerializer } from "./state/custom-route-serializer";
import { CommonModule } from "@angular/common";

import { JhaScreenModule } from "./modules/jha-screen/jha-screen.module";

import { QuillModule } from "ngx-quill";
import { RolesGuard } from "./services/roles.guard";

if (environment.production) {
  enableProdMode();
}

@NgModule({
  declarations: [
    AppComponent,
    MainHeaderComponent,
    MainNavComponent,
    HomeComponent,
  ],
  imports: [
    BrowserModule,
    AuthenticationModule,
    CoreModule,
    CommonModule,
    HttpClientModule,
    JhaButtonsModule,
    JhaDialogBoxModule,
    JhaFloatingToolbarModule,
    JhaFormsModule.forRoot(),
    JhaHeaderModule,
    JhaIconModule,
    JhaLayoutModule,
    JhaMasterDetailModule,
    JhaMegaMenuModule,
    JhaNavModule,
    JhaNotificationsModule,
    JhaPasswordStrengthMeterModule,
    JhaPercentageCircleModule,
    JhaPipesModule,
    JhaProcessTimelineModule,
    JhaProgressBarModule,
    JhaRatingModule,
    JhaRecordDetailModule,
    JhaResponsiveCoreModule.forRoot(),
    JhaTileModule,
    JhaToggleModule,
    JhaUnsavedChangesModule.forRoot(),
    AppRoutingModule,
    TableModule,
    BrowserAnimationsModule,
    ErrorHandlerModule,
    StoreRouterConnectingModule.forRoot({
      serializer: CustomRouteSerializer,
    }),
    StoreModule.forRoot(fromRoot.reducers),
    EffectsModule.forRoot(),
    StoreDevtoolsModule.instrument({
      maxAge: 25,
      logOnly: environment.production,
      connectInZone: true,
    }),
    JhaScreenModule,
    QuillModule.forRoot({
      theme: "snow",
    }),
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  providers: [
    LoggedInGuard,
    RolesGuard,
    {
      provide: HTTP_INTERCEPTORS,
      useClass: ErrorInterceptorService,
      multi: true,
    },
    JhaSessionTimeoutService,
    JhaNotificationService,
  ],
  bootstrap: [AppComponent],
})
export class AppModule {}
